/* CUSTOM CODE for RUGETTE -- START */
import { Component } from '@theme/component';

/**
 * @typedef {object} TestimonialQuotesRefs
 * @property {HTMLElement[]} items - The quote figures, stacked in one grid cell
 */

/**
 * Cycles through stacked testimonial quotes with a cross-fade. Pauses while
 * hovered or focused, and does not autoplay when the user prefers reduced motion.
 *
 * @extends {Component<TestimonialQuotesRefs>}
 */
class TestimonialQuotes extends Component {
  requiredRefs = ['items'];

  #index = 0;

  /** @type {number | undefined} */
  #timer;

  #paused = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { items } = this.refs;
    items.forEach((item, index) => item.classList.toggle('is-active', index === 0));

    if (items.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { signal } = this.#abortController;
    const pause = () => (this.#paused = true);
    const resume = () => (this.#paused = false);
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resume, { signal });

    const seconds = Number(this.dataset.interval) || 6;
    this.#timer = window.setInterval(() => {
      if (!this.#paused && !document.hidden) this.#next();
    }, seconds * 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  #next() {
    const { items } = this.refs;
    const current = items[this.#index];
    this.#index = (this.#index + 1) % items.length;
    const next = items[this.#index];

    current?.classList.remove('is-active');
    current?.setAttribute('aria-hidden', 'true');
    next?.classList.add('is-active');
    next?.removeAttribute('aria-hidden');
  }
}

if (!customElements.get('testimonial-quotes')) {
  customElements.define('testimonial-quotes', TestimonialQuotes);
}
/* CUSTOM CODE for RUGETTE -- END */
